import React, { useState, useEffect, useRef } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Table,
    FormGroup,
    Label,
    Spinner,
    Alert,
    Card,
    CardBody,
    Row,
    Col,
    Badge,
    InputGroup,
    InputGroupText,
    Form,
    FormText
} from 'reactstrap';
import axios from 'axios';

interface OrderLine {
    id: string;
    rowid: string;
    fk_product: string;
    product_ref: string;
    product_label: string;
    description: string;
    qty: string;
    subprice: string;
    total_ht: string;
    total_ttc: string;
    tva_tx: string;
    remise_percent: string;
    rang: string;
    product_barcode?: string;
    fk_commandefourndet?: string | null; // Champ pour identifier la commande fournisseur
}

interface ClientInfo {
    clientid: string;
    nom?: string;
    name_alias?: string;
    email?: string;
    phone?: string;
    address?: string;
    zip?: string;
    town?: string;
}

interface OrderData {
    data: {
        id: string;
        ref: string;
        date: number;
        date_commande: number;
        date_validation: number;
        fk_soc: string;
        total_ht: string;
        total_ttc: string;
        status: string;
        cond_reglement: string;
        lines: OrderLine[];
        expeditions?: { [key: string]: string };
        last_main_doc: string;
        thirdparty?: {
            nom: string;
            name_alias: string;
            email: string;
            phone: string;
            address: string;
            zip: string;
            town: string;
        };
    };
    client?: ClientInfo;
    message: string;
    success: boolean;
}

interface CancellationLine {
    idline: string;
    qty_annule: number;
    annule_commande_four: number;
    reason?: string;
}

interface CancellationResponse {
    success: boolean;
    message: string;
    order?: any;
    order_id?: string;
    order_ref?: string;
    order_status?: string;
    order_cancelled?: boolean;
    lines_processed?: number;
    totals?: {
        initial: {
            total_ht: number;
            total_tva: number;
            total_ttc: number;
            total_qty: number;
        };
        new: {
            total_ht: number;
            total_tva: number;
            total_ttc: number;
            total_qty: number;
        };
    };
    lines_details?: {
        [key: string]: {
            action: string;
            product_id: string;
            product_ref: string;
            old_qty_command: string;
            new_qty_command: number;
            qty_cancelled: number;
            old_qty_expedition: number;
            new_qty_expedition: number;
            is_full_cancel: boolean;
            supplier_order_handling: string;
            supplier_order_details: any;
            has_supplier_order: boolean;
            qty_commande_fournisseur: number;
            ref_commande_fournisseur: string | null;
            id_commande_fournisseur: string | null;
            available_for_cancel: number;
            cancel_supplier_order: number;
            max_cancellable: number;
            reason_saved: boolean;
            reason: string;
        };
    };
    warnings?: string[];
}

interface CommandeListItem {
    id: number;
    ref: string;
    type: string;
    label: string;
    date?: string;
    client_name?: string;
}

interface SearchResponse {
    success: boolean;
    message: string;
    data: CommandeListItem[];
    term: string;
    entity: number;
    entityname: string;
}

interface AnnulationCommandeModalProps {
    isOpen: boolean;
    toggle: () => void;
    user: any;
    orderData?: any;
}

const AnnulationCommandeModal: React.FC<AnnulationCommandeModalProps> = ({ isOpen, toggle, user, orderData: initialOrderData }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<CommandeListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [orderData, setOrderData] = useState<OrderData | null>(initialOrderData || null);
    const [selectedCommande, setSelectedCommande] = useState<CommandeListItem | null>(null);
    const [quantitesAnnule, setQuantitesAnnule] = useState<{ [key: string]: number }>({});
    const [cancelFournisseur, setCancelFournisseur] = useState<{ [key: string]: boolean }>({});
    const [motifsAnnulation, setMotifsAnnulation] = useState<{ [key: string]: string }>({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [validationResponse, setValidationResponse] = useState<CancellationResponse | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const searchTimeoutRef = useRef<number | null>(null);
    const searchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (initialOrderData) {
            setOrderData(initialOrderData);
            setSelectedCommande({
                id: parseInt(initialOrderData.data?.id || '0'),
                ref: initialOrderData.data?.ref || '',
                type: 'commande',
                label: `Commande ${initialOrderData.data?.ref || ''}`
            });
            setSearchTerm(initialOrderData.data?.ref || '');

            const initialQuantites: { [key: string]: number } = {};
            const initialCancelFournisseur: { [key: string]: boolean } = {};
            const initialMotifs: { [key: string]: string } = {};

            if (initialOrderData.data && initialOrderData.data.lines) {
                initialOrderData.data.lines.forEach((line: OrderLine) => {
                    initialQuantites[line.rowid] = 0;
                    // Initialiser la checkbox à false, mais elle sera activée/désactivée selon fk_commandefourndet
                    initialCancelFournisseur[line.rowid] = false;
                    initialMotifs[line.rowid] = '';
                });
            }

            setQuantitesAnnule(initialQuantites);
            setCancelFournisseur(initialCancelFournisseur);
            setMotifsAnnulation(initialMotifs);
        }
    }, [initialOrderData]);

    const getStatusBadge = (statusCode: string) => {
        switch (statusCode) {
            case "-1": return { label: "Annulée", color: "danger" };
            case "0": return { label: "Brouillon", color: "warning" };
            case "1": return { label: "Validée", color: "success" };
            case "2": return { label: "En cours", color: "info" };
            case "3": return { label: "Livrée", color: "success" };
            case "4": return { label: "Processed", color: "primary" };
            default: return { label: "Unknown", color: "secondary" };
        }
    };

    const handleSearchList = async (search: string) => {
        if (search.length < 3) {
            setSearchResults([]);
            setDropdownOpen(false);
            return;
        }

        if (searchTimeoutRef.current) {
            window.clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = window.setTimeout(async () => {
            setSearchLoading(true);
            setDropdownOpen(true);

            const data = {
                action: 'searchref',
                entity: user.entity,
                type: "commande",
                term: search.trim()
            };

            try {
                const response: any = await axios.post<SearchResponse>(
                    'https://api.apapiecesauto.fr/apiV2/',
                    data,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.token}`,
                        }
                    }
                );

                if (response.success) {
                    setSearchResults(response.data);
                    setError('');
                } else {
                    setSearchResults([]);
                    setError(response.message || 'Aucune commande trouvée');
                }
            } catch (error) {
                console.error('Error searching commandes:', error);
                setSearchResults([]);
                setError('Erreur lors de la recherche des commandes');
            } finally {
                setSearchLoading(false);
            }
        }, 300);
    };

    const handleCommandeSelect = async (commande: CommandeListItem) => {
        setSelectedCommande(commande);
        setSearchTerm(commande.ref);
        setSearchResults([]);
        setDropdownOpen(false);
        setError('');
        await handleSearch(commande.ref);
    };

    const handleSearch = async (commandeRef: string) => {
        if (!commandeRef.trim()) {
            setError('Veuillez sélectionner une commande');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setOrderData(null);
        setQuantitesAnnule({});
        setCancelFournisseur({});
        setMotifsAnnulation({});

        try {
            const response: any = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'orderdetails',
                    entity: user.entity,
                    ref: commandeRef.trim()
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.success && response.data) {
                setOrderData(response);

                const initialQuantites: { [key: string]: number } = {};
                const initialCancelFournisseur: { [key: string]: boolean } = {};
                const initialMotifs: { [key: string]: string } = {};
                
                response.data.lines.forEach((line: OrderLine) => {
                    initialQuantites[line.rowid] = 0;
                    // Checkbox initialisée à false
                    initialCancelFournisseur[line.rowid] = false;
                    initialMotifs[line.rowid] = '';
                });
                
                setQuantitesAnnule(initialQuantites);
                setCancelFournisseur(initialCancelFournisseur);
                setMotifsAnnulation(initialMotifs);

            } else {
                setError(response.message || 'Aucun bon de commande trouvé');
            }
        } catch (error) {
            console.error('Error searching order:', error);
            setError('Erreur lors de la recherche du bon de commande');
        } finally {
            setLoading(false);
        }
    };

    const handleQuantiteAnnuleChange = (lineId: string, value: string) => {
        if (value === '') {
            setQuantitesAnnule(prev => ({
                ...prev,
                [lineId]: 0
            }));
            return;
        }

        const numValue = parseInt(value);
        if (isNaN(numValue)) return;

        const line = orderData?.data.lines.find(l => l.rowid === lineId);
        if (!line) return;

        const qtyCommande = parseInt(line.qty);
        const qtyExpedition = orderData?.data?.expeditions?.[lineId] ? parseInt(orderData.data.expeditions[lineId]) : 0;
        
        // Si quantité expédiée > 0, la quantité à annuler devient le reste
        let maxQty = qtyCommande;
        if (qtyExpedition > 0) {
            maxQty = Math.max(0, qtyCommande - qtyExpedition);
            // Si le reste est 0, on ne peut rien annuler
            if (maxQty === 0) {
                setQuantitesAnnule(prev => ({
                    ...prev,
                    [lineId]: 0
                }));
                setError(`Cette ligne a déjà été expédiée (${qtyExpedition} unités). La quantité restante est 0.`);
                return;
            }
        }

        // Validation : ne peut pas dépasser la quantité commandée
        if (numValue >= 0 && numValue <= maxQty) {
            setQuantitesAnnule(prev => ({
                ...prev,
                [lineId]: numValue
            }));
            setError('');
        } else if (numValue > maxQty) {
            setQuantitesAnnule(prev => ({
                ...prev,
                [lineId]: maxQty
            }));
            setError(`Quantité maximale autorisée : ${maxQty}`);
        } else if (numValue < 0) {
            setQuantitesAnnule(prev => ({
                ...prev,
                [lineId]: 0
            }));
        }
    };

    const handleInputFocus = (lineId: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (quantitesAnnule[lineId] === 0) {
            e.target.value = '';
        }
    };

    const handleInputBlur = (lineId: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value === '') {
            setQuantitesAnnule(prev => ({
                ...prev,
                [lineId]: 0
            }));
        }
    };

    const handleCancelFournisseurChange = (lineId: string, checked: boolean) => {
        setCancelFournisseur(prev => ({
            ...prev,
            [lineId]: checked
        }));
    };

    const handleMotifChange = (lineId: string, value: string) => {
        setMotifsAnnulation(prev => ({
            ...prev,
            [lineId]: value
        }));
    };

    const handleValidate = async () => {
        if (!orderData) {
            setError('Aucun bon de commande sélectionné');
            return;
        }

        const hasQuantities = orderData.data.lines.some(line => {
            const qty = quantitesAnnule[line.rowid] || 0;
            return qty > 0;
        });

        if (!hasQuantities) {
            setError('Veuillez spécifier au moins une quantité à annuler');
            return;
        }

        setProcessing(true);
        setError('');
        setSuccess('');

        try {
            const linesToCancel: CancellationLine[] = [];

            orderData.data.lines.forEach(line => {
                const qtyAnnule = quantitesAnnule[line.rowid] || 0;
                if (qtyAnnule > 0) {
                    const qtyCommande = parseInt(line.qty);
                    const qtyExpedition = orderData.data.expeditions?.[line.rowid] ? parseInt(orderData.data.expeditions[line.rowid]) : 0;
                    
                    // Validation finale
                    if (qtyAnnule > qtyCommande) {
                        throw new Error(`La quantité à annuler (${qtyAnnule}) ne peut pas dépasser la quantité commandée (${qtyCommande}) pour le produit ${line.product_ref}`);
                    }

                    if (qtyExpedition > 0 && qtyAnnule > (qtyCommande - qtyExpedition)) {
                        throw new Error(`La quantité à annuler (${qtyAnnule}) ne peut pas dépasser le reste (${qtyCommande - qtyExpedition}) pour le produit ${line.product_ref} (${qtyExpedition} unités déjà expédiées)`);
                    }

                    linesToCancel.push({
                        idline: line.rowid,
                        qty_annule: qtyAnnule,
                        annule_commande_four: cancelFournisseur[line.rowid] ? 1 : 0,
                        reason: motifsAnnulation[line.rowid] || 'Annulation sans motif spécifié'
                    });
                }
            });

            if (linesToCancel.length === 0) {
                setError('Veuillez spécifier au moins une quantité à annuler');
                setProcessing(false);
                return;
            }

            const requestData = {
                action: 'cancelorderline',
                entity: user.entity,
                order_id: orderData.data.id,
                lines: linesToCancel
            };

            console.log('Envoi de la requête:', requestData);

            const response: any = await axios.post<CancellationResponse>(
                'https://api.apapiecesauto.fr/apiV2/',
                requestData,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.success) {
                setValidationResponse(response);
                setShowValidationModal(true);
                setSuccess(response.message || 'Annulation effectuée avec succès');
                
                // Recharger les données de la commande après annulation
                if (orderData.data.ref) {
                    await handleSearch(orderData.data.ref);
                }
            } else {
                setError(response.message || 'Erreur lors de l\'annulation');
            }
        } catch (error: any) {
            console.error('Error processing cancellation:', error);
            setError(error.message || 'Erreur lors du traitement de l\'annulation');
        } finally {
            setProcessing(false);
        }
    };

    const calculateTotalAnnule = () => {
        if (!orderData) return 0;

        return orderData.data.lines.reduce((total, line) => {
            const lineQty = quantitesAnnule[line.rowid] || 0;
            const unitPrice = parseFloat(line.subprice);
            const discount = parseFloat(line.remise_percent) || 0;
            const discountedPrice = unitPrice * (1 - discount / 100);
            const tvaRate = parseFloat(line.tva_tx) || 0;
            const totalHT = lineQty * discountedPrice;
            const totalTVA = totalHT * (tvaRate / 100);
            return total + totalHT + totalTVA;
        }, 0);
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp * 1000).toLocaleDateString('fr-FR');
    };

    const getClientInfo = () => {
        if (!orderData) {
            return {
                nom: 'Client non disponible',
                name_alias: '',
                email: '',
                phone: '',
                address: '',
                zip: '',
                town: ''
            };
        }

        if (orderData.data?.thirdparty) {
            return orderData.data.thirdparty;
        }

        if (orderData.client?.clientid) {
            return {
                nom: orderData.client.nom || `Client ID: ${orderData.client.clientid}`,
                name_alias: orderData.client.name_alias || '',
                email: orderData.client.email || '',
                phone: orderData.client.phone || '',
                address: orderData.client.address || '',
                zip: orderData.client.zip || '',
                town: orderData.client.town || ''
            };
        }

        return {
            nom: 'Client non disponible',
            name_alias: '',
            email: '',
            phone: '',
            address: '',
            zip: '',
            town: ''
        };
    };

    const resetModal = () => {
        setSearchTerm('');
        setSearchResults([]);
        setSelectedCommande(null);
        setOrderData(null);
        setQuantitesAnnule({});
        setCancelFournisseur({});
        setMotifsAnnulation({});
        setError('');
        setSuccess('');
        setLoading(false);
        setSearchLoading(false);
        setProcessing(false);
        setShowValidationModal(false);
        setDropdownOpen(false);
        toggle();
    };

    const closeValidationModal = () => {
        setShowValidationModal(false);
        setValidationResponse(null);
    };

    // Fonction pour vérifier si la ligne a une commande fournisseur
    const hasSupplierOrder = (line: OrderLine): boolean => {
        // Si fk_commandefourndet existe et n'est pas null, alors il y a une commande fournisseur
        return line.fk_commandefourndet !== null && line.fk_commandefourndet !== undefined && line.fk_commandefourndet !== '';
    };

    const clientInfo = orderData ? getClientInfo() : null;

    return (
        <>
            <Modal isOpen={isOpen} toggle={resetModal} size="xl" centered>
                <ModalHeader toggle={resetModal}>
                    <i className="ri-close-circle-line me-2"></i>
                    Annulation de Commande
                </ModalHeader>

                <ModalBody>
                    {/* Search Section with Dropdown */}
                    <FormGroup>
                        <Label for="searchCommande" className="fw-bold mb-2">
                            Rechercher une Commande
                        </Label>
                        <div className="position-relative" ref={searchRef}>
                            <InputGroup>
                                <InputGroupText className="bg-light">
                                    <i className="ri-search-line"></i>
                                </InputGroupText>
                                <Input
                                    type="text"
                                    id="searchCommande"
                                    placeholder="Entrez au moins 3 caractères..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        handleSearchList(e.target.value);
                                    }}
                                    disabled={loading || processing}
                                    className="border-start-0"
                                />
                                {searchLoading && (
                                    <InputGroupText className="bg-white border-start-0">
                                        <Spinner size="sm" />
                                    </InputGroupText>
                                )}
                            </InputGroup>

                            {/* Search Results Dropdown */}
                            {dropdownOpen && searchResults.length > 0 && (
                                <div
                                    className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                                    style={{
                                        zIndex: 1000,
                                        maxHeight: '250px',
                                        overflowY: 'auto'
                                    }}
                                >
                                    <div className="p-1">
                                        {searchResults.map((commande) => (
                                            <div
                                                key={commande.id}
                                                className="p-2 border-bottom hover-pointer"
                                                onClick={() => handleCommandeSelect(commande)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong className="text-primary">{commande.ref}</strong>
                                                        <div className="small text-muted">{commande.label}</div>
                                                        {commande.client_name && (
                                                            <div className="small text-muted">
                                                                <i className="ri-user-line me-1"></i>
                                                                {commande.client_name}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <i className="ri-arrow-right-s-line text-muted"></i>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {dropdownOpen && searchTerm.length >= 3 && !searchLoading && searchResults.length === 0 && (
                                <div
                                    className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-3 text-center"
                                    style={{ zIndex: 1000 }}
                                >
                                    <i className="ri-search-line fs-4 text-muted mb-2 d-block"></i>
                                    <div className="text-muted small">Aucune commande trouvée</div>
                                </div>
                            )}
                        </div>
                        
                        {selectedCommande && (
                            <div className="mt-2">
                                <Alert color="info" className="py-2 mb-0">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>Commande sélectionnée :</strong> {selectedCommande.ref}
                                        </div>
                                        <Button
                                            color="link"
                                            size="sm"
                                            onClick={() => {
                                                setSelectedCommande(null);
                                                setSearchTerm('');
                                                setOrderData(null);
                                            }}
                                            className="p-0"
                                        >
                                            <i className="ri-close-line"></i>
                                        </Button>
                                    </div>
                                </Alert>
                            </div>
                        )}
                    </FormGroup>

                    {/* Alerts */}
                    {error && <Alert color="danger" className="py-2">{error}</Alert>}
                    {success && <Alert color="success" className="py-2">{success}</Alert>}

                    {/* Loading Spinner for Order Details */}
                    {loading && (
                        <div className="text-center my-4">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">Chargement des détails de la commande...</div>
                        </div>
                    )}

                    {/* Order Information */}
                    {orderData && !loading && (
                        <Card className="mt-3 border-primary">
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <div className="d-flex align-items-center mb-2">
                                            <Badge color="primary" className="me-2 fs-14">
                                                Commande
                                            </Badge>
                                            <strong className="fs-16">{orderData.data.ref}</strong>
                                            <Badge color={getStatusBadge(orderData.data.status).color} className="ms-2">
                                                {getStatusBadge(orderData.data.status).label}
                                            </Badge>
                                        </div>
                                        <div className="text-muted small">
                                            <div>Date : {formatDate(orderData.data.date)}</div>
                                            <div>Date validation : {formatDate(orderData.data.date_validation)}</div>
                                        </div>
                                    </Col>
                                    <Col md={6} className="text-end">
                                        <div className="h5 mb-0 text-primary">
                                            Montant total à annuler : {calculateTotalAnnule().toFixed(2)} €
                                        </div>
                                    </Col>
                                </Row>

                                {/* Client Information */}
                                {clientInfo && clientInfo.nom && (
                                    <Row className="mb-3 p-3 bg-light rounded">
                                        <Col>
                                            <h6 className="fw-bold mb-2">Client</h6>
                                            <Row>
                                                <Col md={6}>
                                                    <strong>{clientInfo.nom}</strong>
                                                    {clientInfo.name_alias && (
                                                        <div className="text-muted small">
                                                            Alias: {clientInfo.name_alias}
                                                        </div>
                                                    )}
                                                </Col>
                                                <Col md={6}>
                                                    {clientInfo.email && (
                                                        <div className="small">
                                                            <i className="ri-mail-line me-1"></i>
                                                            {clientInfo.email}
                                                        </div>
                                                    )}
                                                    {clientInfo.phone && (
                                                        <div className="small">
                                                            <i className="ri-phone-line me-1"></i>
                                                            {clientInfo.phone}
                                                        </div>
                                                    )}
                                                    {(clientInfo.address || clientInfo.zip || clientInfo.town) && (
                                                        <div className="small">
                                                            <i className="ri-map-pin-line me-1"></i>
                                                            {[clientInfo.address, clientInfo.zip, clientInfo.town].filter(Boolean).join(' ')}
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                )}

                                <div className="table-responsive">
                                    <Table bordered size="sm" className="mb-0">
                                        <thead className="table-light" style={{ fontSize: '0.75rem' }}>
                                            <tr>
                                                <th style={{ width: '20%' }}>Produit</th>
                                                <th className="text-center">Référence</th>
                                                <th className="text-center">PU HT</th>
                                                <th className="text-center">Qte Commandée</th>
                                                <th className="text-center">Qte Expédiée</th>
                                                <th className="text-center">Qte à Annuler</th>
                                                <th className="text-center">Motif d'Annulation</th>
                                                <th className="text-center">Annuler Commande Fournisseur</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const filteredLines = orderData.data.lines.filter(line => Number(line.qty) > 0);

                                                if (filteredLines.length === 0) {
                                                    return (
                                                        <tr>
                                                            <td colSpan={8} className="text-center py-5">
                                                                <div className="text-muted">
                                                                    <i className="ri-information-line fs-2 d-block mb-2"></i>
                                                                    <strong>Aucun produit dans la commande</strong>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                }

                                                return filteredLines.map((line) => {
                                                    const qtyCommande = parseInt(line.qty);
                                                    const qtyExpedition = orderData.data.expeditions?.[line.rowid] 
                                                        ? parseInt(orderData.data.expeditions[line.rowid]) 
                                                        : 0;
                                                    const maxQty = qtyExpedition > 0 
                                                        ? Math.max(0, qtyCommande - qtyExpedition)
                                                        : qtyCommande;
                                                    const qtyAnnule = quantitesAnnule[line.rowid] || 0;
                                                    const motif = motifsAnnulation[line.rowid] || '';
                                                    const hasSupplierOrderValue = hasSupplierOrder(line);
                                                    const isCheckboxDisabled = !hasSupplierOrderValue || qtyAnnule === 0;

                                                    return (
                                                        <tr key={line.rowid}>
                                                            <td style={{ fontSize: '0.8rem' }}>
                                                                <div className="fw-medium">{line.product_label}</div>
                                                                <small className="text-muted">{line.description}</small>
                                                                {line.product_barcode && (
                                                                    <div className="small text-muted">
                                                                        Code barre: {line.product_barcode}
                                                                    </div>
                                                                )}
                                                                {hasSupplierOrderValue && (
                                                                    <div className="small text-info mt-1">
                                                                        <i className="ri-truck-line me-1"></i>
                                                                        Commande fournisseur associée
                                                                        {line.fk_commandefourndet && (
                                                                            <span className="ms-1"></span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="text-center" style={{ fontSize: '0.8rem' }}>
                                                                <Badge color="success">{line.product_ref}</Badge>
                                                            </td>
                                                            <td className="text-center fw-bold text-success" style={{ fontSize: '0.8rem' }}>
                                                                {parseFloat(line.subprice).toFixed(2)} €
                                                                {parseFloat(line.remise_percent) > 0 && (
                                                                    <div className="small text-danger">
                                                                        -{line.remise_percent}%
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="text-center fw-bold" style={{ fontSize: '0.8rem' }}>
                                                                {qtyCommande}
                                                            </td>
                                                            <td className="text-center" style={{ fontSize: '0.8rem' }}>
                                                                {qtyExpedition > 0 ? (
                                                                    <Badge color="info">{qtyExpedition}</Badge>
                                                                ) : (
                                                                    <span className="text-muted">0</span>
                                                                )}
                                                            </td>
                                                            <td style={{ width: '100px' }}>
                                                                <div className="d-flex flex-column align-items-center">
                                                                    <Input
                                                                        type="number"
                                                                        min="0"
                                                                        max={maxQty}
                                                                        value={qtyAnnule === 0 ? 0 : qtyAnnule}
                                                                        onChange={(e) => handleQuantiteAnnuleChange(line.rowid, e.target.value)}
                                                                        onFocus={(e) => handleInputFocus(line.rowid, e)}
                                                                        onBlur={(e) => handleInputBlur(line.rowid, e)}
                                                                        disabled={processing || maxQty === 0}
                                                                        className="text-center"
                                                                        style={{
                                                                            textAlign: 'center',
                                                                            fontSize: '0.8rem',
                                                                            padding: '0.25rem',
                                                                            height: '30px'
                                                                        }}
                                                                    />
                                                                    {qtyExpedition > 0 && (
                                                                        <FormText color="info" className="small mt-1">
                                                                            Reste : {maxQty}
                                                                        </FormText>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td style={{ width: '150px' }}>
                                                                <Input
                                                                    type="text"
                                                                    placeholder="Motif d'annulation..."
                                                                    value={motif}
                                                                    onChange={(e) => handleMotifChange(line.rowid, e.target.value)}
                                                                    disabled={processing || maxQty === 0 || qtyAnnule === 0}
                                                                    className="small"
                                                                    style={{ 
                                                                        fontSize: '0.8rem', 
                                                                        padding: '0.25rem',
                                                                        height: '30px'
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className="text-center" style={{ width: '80px' }}>
                                                                <Form check className="d-flex justify-content-center">
                                                                    <Input
                                                                        type="checkbox"
                                                                        checked={cancelFournisseur[line.rowid] || false}
                                                                        onChange={(e) => handleCancelFournisseurChange(line.rowid, e.target.checked)}
                                                                        disabled={processing || isCheckboxDisabled}
                                                                        id={`cancel-four-${line.rowid}`}
                                                                        title={isCheckboxDisabled ? 
                                                                            (!hasSupplierOrderValue ? 
                                                                                "Pas de commande fournisseur associée" : 
                                                                                "Quantité à annuler = 0") : 
                                                                            "Annuler la commande fournisseur associée"}
                                                                    />
                                                                    <Label
                                                                        check
                                                                        for={`cancel-four-${line.rowid}`}
                                                                        className="small ms-1"
                                                                    >
                                                                        Fournisseur
                                                                    </Label>
                                                                </Form>
                                                                {!hasSupplierOrderValue && (
                                                                    <div className="small text-muted mt-1">
                                                                        Non applicable
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            })()}
                                        </tbody>
                                    </Table>
                                </div>

                                {(() => {
                                    const hasLinesWithQty = orderData.data.lines.some(line => Number(line.qty) > 0);
                                    if (!hasLinesWithQty) return null;

                                    return (
                                        <div className="mt-3 p-3 bg-light rounded">
                                            <Row className="text-center align-items-center">
                                                <Col md={4}>
                                                    <div className="text-muted small">Montant total à annuler</div>
                                                    <div className="h3 text-primary fw-bold mb-0">
                                                        {calculateTotalAnnule().toFixed(2)} €
                                                    </div>
                                                    <small className="text-muted">TTC</small>
                                                </Col>
                                                <Col md={4}>
                                                    <div className="text-muted small">Statut</div>
                                                    <Badge color={getStatusBadge(orderData.data.status).color} className="ms-2">
                                                        {getStatusBadge(orderData.data.status).label}
                                                    </Badge>
                                                </Col>
                                                <Col md={4}>
                                                    <Button
                                                        color="danger"
                                                        onClick={handleValidate}
                                                        disabled={processing}
                                                        className="w-100"
                                                    >
                                                        {processing ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Traitement en cours...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-close-circle-line me-2"></i>
                                                                Valider l'Annulation
                                                            </>
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>
                                    );
                                })()}
                            </CardBody>
                        </Card>
                    )}
                </ModalBody>
            </Modal>

            {/* Validation Result Modal */}
            <Modal isOpen={showValidationModal} toggle={closeValidationModal} size="lg" centered>
                <ModalHeader toggle={closeValidationModal}>
                    <i className="ri-information-line me-2"></i>
                    Résultat de l'Annulation
                </ModalHeader>

                <ModalBody>
                    {validationResponse && (
                        <>
                            <Alert
                                color={validationResponse.success ? "success" : "warning"}
                                className="mb-4"
                            >
                                <div className="d-flex align-items-center">
                                    <i className={`ri-${validationResponse.success ? "check" : "error-warning"}-line me-2 fs-5`}></i>
                                    <strong>{validationResponse.message}</strong>
                                </div>
                                {validationResponse.lines_processed !== undefined && (
                                    <div className="mt-2">
                                        <strong>Lignes traitées :</strong> {validationResponse.lines_processed}
                                    </div>
                                )}
                            </Alert>

                            {validationResponse.warnings && validationResponse.warnings.length > 0 && (
                                <Alert color="warning" className="mb-4">
                                    <h6>Avertissements:</h6>
                                    <ul className="mb-0">
                                        {validationResponse.warnings.map((warning, index) => (
                                            <li key={index}>{warning}</li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}

                            {validationResponse.totals && (
                                <Card className="mb-4">
                                    <CardBody>
                                        <h6 className="fw-bold mb-3">Totaux</h6>
                                        <Row>
                                            <Col md={6}>
                                                <div className="mb-2">
                                                    <strong>Avant annulation :</strong>
                                                    <div className="small">
                                                        HT: {validationResponse.totals.initial.total_ht.toFixed(2)} €<br />
                                                        TVA: {validationResponse.totals.initial.total_tva.toFixed(2)} €<br />
                                                        TTC: {validationResponse.totals.initial.total_ttc.toFixed(2)} €<br />
                                                        Quantité: {validationResponse.totals.initial.total_qty}
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="mb-2">
                                                    <strong>Après annulation :</strong>
                                                    <div className="small">
                                                        HT: {validationResponse.totals.new.total_ht.toFixed(2)} €<br />
                                                        TVA: {validationResponse.totals.new.total_tva.toFixed(2)} €<br />
                                                        TTC: {validationResponse.totals.new.total_ttc.toFixed(2)} €<br />
                                                        Quantité: {validationResponse.totals.new.total_qty}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}

                            {validationResponse.lines_details && Object.keys(validationResponse.lines_details).length > 0 && (
                                <>
                                    <h6 className="fw-bold mb-3">Détail des lignes traitées:</h6>
                                    <div className="table-responsive">
                                        <Table bordered size="sm">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Produit</th>
                                                    <th className="text-center">Référence</th>
                                                    <th className="text-center">Quantité annulée</th>
                                                    <th className="text-center">Nouvelle quantité</th>
                                                    <th className="text-center">Annulation fournisseur</th>
                                                    <th className="text-center">Motif</th>
                                                    <th className="text-center">Statut</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(validationResponse.lines_details).map(([lineId, lineDetail]) => (
                                                    <tr key={lineId}>
                                                        <td>
                                                            <div className="fw-medium">{lineDetail.product_ref}</div>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge color="info">{lineDetail.product_ref}</Badge>
                                                        </td>
                                                        <td className="text-center fw-bold text-danger">
                                                            {lineDetail.qty_cancelled}
                                                        </td>
                                                        <td className="text-center">
                                                            {lineDetail.old_qty_command} → {lineDetail.new_qty_command}
                                                        </td>
                                                        <td className="text-center">
                                                            {lineDetail.cancel_supplier_order === 1 ? (
                                                                <Badge color="warning">Oui</Badge>
                                                            ) : (
                                                                <Badge color="secondary">Non</Badge>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <small>{lineDetail.reason}</small>
                                                        </td>
                                                        <td className="text-center">
                                                            <i className="ri-checkbox-circle-fill text-success fs-5"></i>
                                                            <div className="small">Succès</div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}

                            {validationResponse.order_ref && (
                                <div className="mt-3 p-3 bg-light rounded">
                                    <div className="text-center">
                                        <strong>Commande : </strong>
                                        {validationResponse.order_ref}
                                        {validationResponse.order_status && (
                                            <>
                                                {' - Statut : '}
                                                <Badge color={getStatusBadge(validationResponse.order_status).color}>
                                                    {getStatusBadge(validationResponse.order_status).label}
                                                </Badge>
                                            </>
                                        )}
                                        {validationResponse.order_cancelled !== undefined && (
                                            <div className="mt-1">
                                                <strong>Commande annulée : </strong>
                                                {validationResponse.order_cancelled ? 'Oui' : 'Non'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={closeValidationModal}>
                        Fermer
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default AnnulationCommandeModal;